import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ContentEditor from "@/components/admin/ContentEditor";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

interface TenantOption { id: string; name: string }
interface LandingPageTemplate { id: string; name: string; description: string | null; category: string }
interface TenantLandingPage {
  id: string;
  tenant_id: string;
  template_id: string | null;
  name: string;
  slug: string;
  is_active: boolean;
  is_default: boolean;
  referrer_message: string | null;
  content_data: any;
  landing_page_templates?: { name: string } | null;
}

export function LandingPagesModule() {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [templates, setTemplates] = useState<LandingPageTemplate[]>([]);
  const [pages, setPages] = useState<TenantLandingPage[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<TenantLandingPage | null>(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  const [newPage, setNewPage] = useState({ name: "", slug: "", template_id: "", referrer_message: "Gerade hat {referrer_name} an deinen Geldbeutel gedacht." });

  useEffect(() => {
    const init = async () => {
      try {
        // Load tenants
        const { data: tenantData, error: tErr } = await supabase
          .from("tenants")
          .select("id, name")
          .order("name");
        if (tErr) throw tErr;
        setTenants(tenantData || []);
        if (!selectedTenant && tenantData && tenantData.length > 0) {
          setSelectedTenant(tenantData[0].id);
        }
        // Load templates
        const { data: tplData, error: tpErr } = await supabase
          .from("landing_page_templates")
          .select("id, name, description, category")
          .eq("is_active", true)
          .order("name");
        if (tpErr) throw tpErr;
        setTemplates(tplData || []);
      } catch (e) {
        console.error(e);
        toast.error("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;
    const loadPages = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("tenant_landing_pages")
          .select(`*, landing_page_templates(name)`) 
          .eq("tenant_id", selectedTenant)
          .order("created_at", { ascending: false });
        if (error) throw error;
        setPages(data || []);
      } catch (e) {
        console.error(e);
        toast.error("Fehler beim Laden der Landing Pages");
      } finally {
        setLoading(false);
      }
    };
    loadPages();
  }, [selectedTenant]);

  const selectedTenantName = useMemo(() => tenants.find(t => t.id === selectedTenant)?.name || "-", [tenants, selectedTenant]);

  const createPage = async () => {
    if (!selectedTenant || !newPage.name || !newPage.slug || !newPage.template_id) return;
    try {
      // Hole KI-generierte Inhalte falls vorhanden
      const aiContent = (window as any).__aiGeneratedContent || {};
      delete (window as any).__aiGeneratedContent;

      const { error } = await supabase.from("tenant_landing_pages").insert({
        tenant_id: selectedTenant,
        template_id: newPage.template_id,
        name: newPage.name,
        slug: newPage.slug,
        referrer_message: newPage.referrer_message,
        content_data: aiContent,
        theme_title: aiContent.theme_title,
        target_audience: aiContent.target_audience,
        reward_description: aiContent.reward_description,
        is_active: true,
        is_default: pages.length === 0,
      });
      if (error) throw error;
      toast.success("Landing Page erstellt");
      setCreateOpen(false);
      setNewPage({ name: "", slug: "", template_id: "", referrer_message: "Gerade hat {referrer_name} an deinen Geldbeutel gedacht." });
      // reload
      const { data } = await supabase
        .from("tenant_landing_pages")
        .select(`*, landing_page_templates(name)`) 
        .eq("tenant_id", selectedTenant)
        .order("created_at", { ascending: false });
      setPages(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Erstellen");
    }
  };

  const toggleActive = async (pageId: string, value: boolean) => {
    try {
      const { error } = await supabase.from("tenant_landing_pages").update({ is_active: value }).eq("id", pageId);
      if (error) throw error;
      setPages(prev => prev.map(p => p.id === pageId ? { ...p, is_active: value } : p));
    } catch (e) {
      console.error(e);
      toast.error("Status konnte nicht geändert werden");
    }
  };

  const saveContent = async (page: TenantLandingPage, content: any) => {
    try {
      const { error } = await supabase.from("tenant_landing_pages").update({ content_data: content }).eq("id", page.id);
      if (error) throw error;
      toast.success("Inhalt gespeichert");
    } catch (e) {
      console.error(e);
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const generateWithAI = async () => {
    if (!aiPrompt.trim() || !selectedTenant) {
      toast.error("Bitte geben Sie eine Beschreibung ein");
      return;
    }

    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-landing-page-content', {
        body: {
          prompt: aiPrompt,
          tenant_name: selectedTenantName,
          template_category: templates.find(t => t.id === newPage.template_id)?.category
        }
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      const generatedContent = data.content;
      
      // Fülle das Formular mit den generierten Daten
      setNewPage({
        name: generatedContent.name || "",
        slug: generatedContent.slug || "",
        template_id: newPage.template_id,
        referrer_message: generatedContent.referrer_message || "Gerade hat {referrer_name} an deinen Geldbeutel gedacht."
      });

      // Speichere die zusätzlichen Inhalte für später
      (window as any).__aiGeneratedContent = generatedContent;

      toast.success("Landing Page mit KI generiert!");
      setAiDialogOpen(false);
      setCreateOpen(true);
      
    } catch (e: any) {
      console.error("AI Generation Error:", e);
      toast.error(e.message || "Fehler bei der KI-Generierung");
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Landing Pages verwalten</CardTitle>
        <CardDescription>Erstellen, bearbeiten und veröffentlichen Sie Landing Pages je Tenant.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <Label>Tenant auswählen</Label>
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger>
                <SelectValue placeholder="Tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Sparkles className="h-4 w-4 mr-2" /> Mit KI erstellen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Landing Page mit KI generieren</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Template</Label>
                    <Select value={newPage.template_id} onValueChange={(v) => setNewPage({ ...newPage, template_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Template auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} {t.category && `(${t.category})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Beschreiben Sie die gewünschte Landing Page</Label>
                    <Textarea 
                      placeholder="z.B. Landing Page für ein Restaurant mit italienischer Küche. Zielgruppe sind Familien und Paare. Die Belohnung ist ein kostenloser Nachtisch bei der nächsten Bestellung..."
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      rows={6}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Je detaillierter die Beschreibung, desto besser das Ergebnis
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setAiDialogOpen(false)}
                      disabled={aiGenerating}
                    >
                      Abbrechen
                    </Button>
                    <Button 
                      onClick={generateWithAI} 
                      disabled={!aiPrompt.trim() || !newPage.template_id || aiGenerating}
                    >
                      {aiGenerating ? (
                        <>
                          <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                          Generiere...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generieren
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" /> Neue Landing Page
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Neue Landing Page ({selectedTenantName})</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Template</Label>
                    <Select value={newPage.template_id} onValueChange={(v) => setNewPage({ ...newPage, template_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Template auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input value={newPage.name} onChange={(e) => setNewPage({ ...newPage, name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Slug</Label>
                      <Input value={newPage.slug} onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
                    </div>
                  </div>
                  <div>
                    <Label>Empfehler-Nachricht</Label>
                    <Input value={newPage.referrer_message} onChange={(e) => setNewPage({ ...newPage, referrer_message: e.target.value })} />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={createPage} disabled={!newPage.name || !newPage.slug || !newPage.template_id}>Erstellen</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map(p => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">/lp/…/{p.slug}</div>
                  </TableCell>
                  <TableCell>{p.landing_page_templates?.name || "-"}</TableCell>
                  <TableCell>{p.slug}</TableCell>
                  <TableCell>
                    {p.is_active ? <Badge>Aktiv</Badge> : <Badge variant="secondary">Inaktiv</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingPage(p); setEditOpen(true); }}>
                        <Edit className="h-4 w-4 mr-1" /> Bearbeiten
                      </Button>
                      <Switch checked={p.is_active} onCheckedChange={(v) => toggleActive(p.id, v)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>Inhalt bearbeiten: {editingPage?.name}</DialogTitle>
            </DialogHeader>
            {editingPage && (
              <div className="space-y-4">
                <ContentEditor
                  contentData={editingPage.content_data}
                  onUpdate={(updated) => {
                    setEditingPage({ ...editingPage, content_data: updated });
                    saveContent(editingPage, updated);
                  }}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
